import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactTabulator } from 'react-tabulator';
import 'tabulator-tables/dist/css/tabulator.min.css';
import BlankPageTemplate from '../Templates/BlankPageTemplate';
import ContainerFluid from '../ThemeComponents/grid/ContainerFluid';
import Row from '../ThemeComponents/grid/Row';
import Col from '../ThemeComponents/grid/Col';
import { vehiclesMock } from '../mock/vehicles-mock';

const dayOrder = [1, 2, 3, 4, 5, 6, 7];
const dayLabels = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun',
};

const buildScheduleSummary = (workingHours) => {
  if (!Array.isArray(workingHours) || workingHours.length === 0) {
    return '-';
  }

  const activeDays = workingHours
    .filter((item) => item?.is_active)
    .map((item) => dayLabels[item?.day_of_week])
    .filter(Boolean);

  if (activeDays.length === 0) {
    return 'Inactive';
  }

  if (activeDays.length === 7) {
    return 'Mon-Sun';
  }

  return activeDays.join(' ');
};

const buildLocationLabel = (location) => {
  if (!location) {
    return '-';
  }
  const parts = [
    location.zip,
    location.city,
    location.street,
    location.house_number,
  ].filter(Boolean);
  return parts.length ? parts.join(' ') : '-';
};

const buildModelLabel = (model) => {
  if (!model) {
    return '-';
  }
  const parts = [model.mark, model.model].filter(Boolean);
  return parts.length ? parts.join(' ') : '-';
};

const renderScheduleBadges = (workingHours) => {
  const activeSet = new Set(
    (workingHours || [])
      .filter((item) => item?.is_active)
      .map((item) => item?.day_of_week)
  );

  return dayOrder
    .map((day) => {
      const badgeClass = activeSet.has(day) ? 'text-bg-success' : 'text-bg-secondary';
      return `<span class="badge ${badgeClass} me-1">${dayLabels[day]}</span>`;
    })
    .join('');
};

const renderPhoto = (url) => {
  if (!url) {
    return '<span class="text-muted">No photo</span>';
  }
  return `<img src="${url}" alt="Vehicle" style="width:64px;height:40px;object-fit:cover;border-radius:4px;" />`;
};

const tableData = vehiclesMock.map((vehicle) => ({
  ...vehicle,
  modelLabel: buildModelLabel(vehicle.model),
  locationLabel: buildLocationLabel(vehicle.location),
  scheduleSummary: buildScheduleSummary(vehicle.working_hours),
}));

const buildColumns = (navigate) => [
  { title: 'ID', field: 'id', sorter: 'number', headerFilter: 'input', width: 80 },
  {
    title: 'Photo',
    field: 'main_photo',
    headerSort: false,
    formatter: (cell) => renderPhoto(cell.getValue()),
    width: 90,
  },
  { title: 'Make/Model', field: 'modelLabel', sorter: 'string', headerFilter: 'input', minWidth: 160 },
  { title: 'Plate', field: 'license_plate', sorter: 'string', headerFilter: 'input', width: 120 },
  {
    title: 'Location',
    field: 'locationLabel',
    sorter: 'string',
    headerFilter: 'input',
    minWidth: 200,
  },
  {
    title: 'Schedule',
    field: 'scheduleSummary',
    sorter: 'string',
    headerFilter: 'input',
    formatter: (cell) => renderScheduleBadges(cell.getRow().getData().working_hours),
    minWidth: 220,
  },
  {
    title: 'Actions',
    field: 'actions',
    headerSort: false,
    hozAlign: 'right',
    headerHozAlign: 'right',
    width: 180,
    formatter: () =>
      '<div class="d-flex gap-2 justify-content-end">' +
      '<button type="button" class="btn btn-sm btn-primary" data-action="edit">Edit</button>' +
      '<button type="button" class="btn btn-sm btn-danger" data-action="delete">Delete</button>' +
      '</div>',
    cellClick: (event, cell) => {
      const action = event.target?.dataset?.action;
      if (!action) {
        return;
      }
      const { id } = cell.getRow().getData();
      if (action === 'edit') {
        navigate(`/backoffice/vehicles/${id}`);
      }
    },
  },
];

const Vehicles = () => {
  const navigate = useNavigate();
  const columns = buildColumns(navigate);

  return (
    <BlankPageTemplate title="Vehicles">
      <ContainerFluid>
        <Row>
          <Col cols="col-12">
            <ReactTabulator
              data={tableData}
              columns={columns}
              options={{ layout: 'fitColumns', placeholder: 'No vehicles yet' }}
            />
          </Col>
        </Row>
      </ContainerFluid>
    </BlankPageTemplate>
  );
};

export default Vehicles;
